
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { PaymentExpensesList } from "@/components/payments/PaymentExpensesList";
import { ReceiptUploader } from "@/components/payments/ReceiptUploader";
import { PaymentInfo } from "@/components/payments/PaymentInfo";
import { PaymentLink } from "@/components/payments/PaymentLink";
import { ScrollArea } from "@/components/ui/scroll-area";

const PaymentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: payment, isLoading, error } = useQuery({
    queryKey: ['payment', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('*, houses(name)')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const handleUploadSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['payment', id] });
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-6 w-32" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-500">
              Error al cargar el pago. Por favor intente más tarde.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-gray-500">
              No se encontró el pago solicitado.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-4rem)]">
      <div className="p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Detalles del pago</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <PaymentInfo payment={payment} />
            
            <ReceiptUploader 
              paymentId={id || ''} 
              currentReceiptUrl={payment.receipt_url} 
              onUploadSuccess={handleUploadSuccess}
            />

            {payment.status === 'pending' && payment.payment_link_url && (
              <PaymentLink paymentLinkUrl={payment.payment_link_url} />
            )}
          </CardContent>
        </Card>
        
        {/* Add the expense list component */}
        <PaymentExpensesList paymentId={id || ''} expenseIds={payment.expense_ids} />
      </div>
    </ScrollArea>
  );
};

export default PaymentDetails;
